/**
 * UI Language Configuration.
 *
 * Defines supported languages for the application interface.
 * This is separate from content languages (lesson/activity content).
 */

export interface UILanguage {
  code: string;
  label: string;
  nativeName: string;
  isRTL: boolean;
}

/**
 * Supported UI languages.
 * Only add a language here when translation files are ready.
 */
export const UI_LANGUAGES: UILanguage[] = [
  {
    code: 'en',
    label: 'English',
    nativeName: 'English',
    isRTL: false,
  },
  {
    code: 'he',
    label: 'Hebrew',
    nativeName: 'עברית',
    isRTL: true,
  },
  {
    code: 'ar',
    label: 'Arabic',
    nativeName: 'العربية',
    isRTL: true,
  },
];

/**
 * Language codes array for i18next configuration.
 */
export const SUPPORTED_UI_LANGUAGE_CODES = UI_LANGUAGES.map((lang) => lang.code);

/**
 * Default/fallback UI language.
 */
export const DEFAULT_UI_LANGUAGE = 'en';

/**
 * RTL language codes for quick lookup.
 */
export const RTL_LANGUAGE_CODES = UI_LANGUAGES.filter((lang) => lang.isRTL).map((lang) => lang.code);

/**
 * Check if a language code is RTL.
 */
export const isRTLLanguage = (code: string): boolean => RTL_LANGUAGE_CODES.includes(code);

/**
 * The base language of a BCP-47 tag ("he-IL" -> "he").
 */
export const toBaseLanguage = (code: string): string => code.split('-')[0].toLowerCase();

/**
 * Resolve an i18n language code (or BCP-47 tag like "he-IL") to its
 * human-readable English label. Returns the trimmed code itself when
 * the language is not in the supported list, so callers always get a
 * non-empty string suitable for prompt instructions.
 */
export const resolveLanguageLabel = (code: string | undefined | null): string => {
  if (!code) return UI_LANGUAGES[0].label;
  const base = toBaseLanguage(code);
  const match = UI_LANGUAGES.find((lang) => lang.code === base);
  return match?.label ?? code;
};